import { TicketTypesEnum } from './avia-page.types';

/**
 * Cabin classes of the Avia search (QA doc docs/processes/flight-search-filters.md R-C5..R-C8, cabin classes phase 2, 01.10.2026; approved
 * mockup docs/features/flight-cabin-classes/mockups/cabin-classes-website.html screen A). flights-api answers only fares of the chosen class
 * (R-C1..R-C3). Labels are translation keys = the English text; "First Class" / "Premium Economy" are also the names flights-api gives a
 * segment, so the class badge translates them with the same keys.
 */
export const CABIN_CLASS_OPTIONS: ReadonlyArray<{
  value: TicketTypesEnum;
  label: string;
  hint: string;
  testId: string;
}> = [
  { value: TicketTypesEnum.Coach, label: 'Economy', hint: 'Standard seat', testId: 'economy' },
  {
    value: TicketTypesEnum.Premium,
    label: 'Premium Economy',
    hint: 'More legroom, better meal',
    testId: 'premium-economy'
  },
  { value: TicketTypesEnum.Business, label: 'Business', hint: 'Lie-flat or wide seat, lounge', testId: 'business' },
  { value: TicketTypesEnum.First, label: 'First Class', hint: 'Private suite on some airlines', testId: 'first' }
];

/**
 * flight-search-filters R-C9 / flight-search-progressive R-PF9 (Amiran 20.10.2026, website-web#82): a new flight search ticks Economy only - the
 * agent ticks Premium Economy / Business / First when needed, and each ticked class is still its own search. Replaces the all-classes default of
 * 12.10.2026 (f077c95c), which sent 4 searches per user search. A fresh array each call, so a tick never changes the default.
 */
export const DEFAULT_CABIN_CLASSES = (): TicketTypesEnum[] => [TicketTypesEnum.Coach];

/** The translation key of a requested class; no class = Economy (the search default). */
export const cabinClassLabel = (value: TicketTypesEnum | string | number | null | undefined): string =>
  CABIN_CLASS_OPTIONS.find((o) => o.value === value)?.label ?? 'Economy';

/** R-C8: a class other than Economy was asked - an empty answer means no fares of that class, not "nothing found". */
export const isUpperCabin = (value: TicketTypesEnum | string | number | null | undefined): boolean =>
  !!value && value !== TicketTypesEnum.Coach && CABIN_CLASS_OPTIONS.some((o) => o.value === value);

/** R-C7: an itinerary whose flights carry different classes (e.g. Business out, Economy back). */
export const isMixedCabin = (classes: ReadonlyArray<string | null | undefined>): boolean =>
  new Set(classes.filter((c): c is string => !!c && c.trim().length > 0).map((c) => c.trim().toLowerCase())).size > 1;

/**
 * The classes of a search (docs features/website/avia-class-multi-select R-CM1): the ticked ones in the list order (Economy, Premium,
 * Business, First), duplicates and unknown letters dropped; none ticked -> the single `ticketType`; nothing -> Economy (the default).
 */
export const cabinClassList = (
  list: ReadonlyArray<TicketTypesEnum | string | null | undefined> | null | undefined,
  single?: TicketTypesEnum | string | null
): TicketTypesEnum[] => {
  const picked = new Set<unknown>(list ?? []);
  const ordered = CABIN_CLASS_OPTIONS.filter((o) => picked.has(o.value)).map((o) => o.value);
  if (ordered.length) return ordered;
  const one = CABIN_CLASS_OPTIONS.find((o) => o.value === single)?.value;
  return [one ?? TicketTypesEnum.Coach];
};

/**
 * R-CM2: what the passengers field shows - up to `named` classes by name (`Economy, Business`), more as the first one + the count of the
 * others (`Economy +2`). `short` uses the short names (CABIN_SHORT_*). `keys` are translation keys, `more` is 0 when every class is named.
 */
export const cabinClassSummary = (
  list: ReadonlyArray<TicketTypesEnum | string | null | undefined> | null | undefined,
  single?: TicketTypesEnum | string | null,
  named = 2,
  short = false
): { keys: string[]; more: number } => {
  // the narrow Класс field uses the short names (CABIN_SHORT_*: Эконом / Премиум / Бизнес / Первый) so they fit 8rem
  const labels = cabinClassList(list, single).map((c) => (short ? `CABIN_SHORT_${c}` : cabinClassLabel(c)));
  return labels.length <= named ? { keys: labels, more: 0 } : { keys: [labels[0]], more: labels.length - 1 };
};
